import React, { useRef, useState, useEffect } from 'react';
import ListingCard from '../ListingCard/ListingCard';
import css from './HorizontalListingsGrid.module.css';

/**
 * HorizontalListingsGrid - displays listings in a 2-row horizontal scrolling grid
 * Specifically designed for landing page to show only 2 rows with horizontal scroll
 */
const HorizontalListingsGrid = props => {
  const {
    className,
    rootClassName,
    listings = [],
    searchInProgress,
    searchListingsError,
  } = props;

  const [currentPage, setCurrentPage] = useState(0);

  // Responsive items per page: 4 for mobile (2×2), 10 for desktop (2×5)
  const getItemsPerPage = () => {
    // Check if window is available (client-side only)
    if (typeof window !== 'undefined' && window.innerWidth <= 549) {
      return 4; // Mobile: 2 columns × 2 rows = 4 items
    }
    return 10; // Desktop: 5 columns × 2 rows = 10 items (default for SSR)
  };

  const [itemsPerPage, setItemsPerPage] = useState(10); // Default to desktop for SSR
  
  // Calculate pagination
  const totalPages = Math.ceil(listings.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentListings = listings.slice(startIndex, endIndex);
  
  // Navigation functions
  const canGoLeft = currentPage > 0;
  const canGoRight = currentPage < totalPages - 1;
  
  const goLeft = () => {
    if (canGoLeft) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goRight = () => {
    if (canGoRight) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to first page when listings change
  useEffect(() => {
    setCurrentPage(0);
  }, [listings]);

  // Set correct items per page on client-side mount and handle resize
  useEffect(() => {
    // Set correct items per page on first mount (client-side only)
    const initialItemsPerPage = getItemsPerPage();
    setItemsPerPage(initialItemsPerPage);

    const handleResize = () => {
      const newItemsPerPage = getItemsPerPage();
      if (newItemsPerPage !== itemsPerPage) {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(0); // Reset to first page when changing layout
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array for mount only

  console.log('HorizontalListingsGrid props:', { 
    listings: listings?.length, 
    searchInProgress, 
    searchListingsError 
  });

  if (searchListingsError) {
    return (
      <div className={css.error}>
        <h3 className={css.errorHeading}>Something went wrong. Please try again.</h3>
      </div>
    );
  }

  if (searchInProgress) {
    return (
      <div className={css.loading}>
        <div className={css.spinner}>Loading listings...</div>
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className={css.noResults}>
        <h3 className={css.noResultsHeading}>No listings found</h3>
        <p className={css.noResultsText}>Try adjusting your search criteria</p>
      </div>
    );
  }

  // Responsive sizes for grid layout
  const cardRenderSizes = [
    '(max-width: 549px) 50vw',
    '(max-width: 767px) 33vw', 
    '(max-width: 1023px) 25vw',
    '20vw',
  ].join(', ');

  return (
    <div className={`${css.root} ${rootClassName || ''} ${className || ''}`}>
      <h2 className={css.sectionHeading}>Explore Our Listings</h2>
      <div className={css.paddedContainer}>
        <div className={css.listingsContainer}>
          {/* Navigation arrows */}
          {canGoLeft && (
            <button 
              className={`${css.navArrow} ${css.navArrowLeft}`}
              onClick={goLeft}
              aria-label="Previous page"
            >
              ←
            </button>
          )}
          {canGoRight && (
            <button 
              className={`${css.navArrow} ${css.navArrowRight}`}
              onClick={goRight}
              aria-label="Next page"
            >
              →
            </button>
          )}
          
          <div className={css.listingsGrid}>
            {currentListings.map(listing => (
              <ListingCard
                key={listing.id.uuid}
                className={css.listingCard}
                listing={listing}
                renderSizes={cardRenderSizes}
                showAuthorInfo={false}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorizontalListingsGrid;